import type { Ruleset } from "./types.js";

/**
 * MCS 020 a) — sound assessment for permitted development.
 *
 * England: compliance with MCS 020 a) is a condition of permitted development
 * from 29 May 2025 (SI 2025/560), and applies whether or not the installation is
 * MCS certified. Wales followed from 1 June 2026 (WSI 2026/79).
 *
 * Scotland and Northern Ireland remain on the legacy combined MCS 020 (v1.3),
 * whose 42 dB(A) limit is assessed by a DIFFERENT method (it accounts for
 * background noise). That legacy method is not implemented here — see the
 * provisional ruleset below.
 */

export type BarrierType = "type-1" | "type-2" | "none";
export type LineOfSight = "no-view" | "partial-view" | "full-view";

export interface NoiseRules {
  /** Maximum permitted sound pressure level at the assessment position, dB(A). */
  readonly limitDb: number;
  /** Human-readable metric, e.g. "LAeq,5mins". */
  readonly metric: string;
  /**
   * Barrier attenuation A_B in dB(A), indexed [barrier type][line of sight].
   *
   * Type 1 — solid brick/masonry wall or solid fence at least 18 mm thick, no
   *          cracks or gaps, extending horizontally 1 m or more beyond either side.
   * Type 2 — solid fence less than 18 mm thick, otherwise as above.
   * None   — open fences, fences with gaps/cracks, vegetation, or anything in poor
   *          repair. These provide no attenuation.
   */
  readonly barrierAttenuationDb: Record<BarrierType, Record<LineOfSight, number>>;
  /** Permitted directivity factors. More than three reflecting surfaces fails outright. */
  readonly permittedDirectivityFactors: readonly number[];
  /** Distance is measured to this precision, in metres. */
  readonly distanceRoundingM: number;
  /**
   * Step 4: "A reflective surface is any surface (including the ground, but not
   * vegetation) within 1 m of the heat pump." Metres.
   */
  readonly reflectingSurfaceWithinM: number;
  /**
   * Step 4: "The reflecting surface shall extend beyond the edge of the heat
   * pump by 1m or more, in two or more directions." Metres.
   */
  readonly reflectingSurfaceMinExtentM: number;
  /**
   * Step 4's guide, as a lookup: the directivity factor Q for 1, 2 or 3
   * reflecting surfaces (Q2, Q4, Q8). More than three is not permitted.
   */
  readonly directivityByReflectingSurfaces: Readonly<Record<1 | 2 | 3, number>>;
  /**
   * Step 8: "The calculated sound pressure shall be rounded to the nearest 0.1
   * decimal place" before it is compared with the limit. dB.
   */
  readonly resultRoundingDb: number;
  /**
   * Definitions, "Assessment Position": 1 m external to the centre point of a
   * door or window to a habitable room of a neighbouring property, measured
   * perpendicular to its plane. Metres.
   */
  readonly assessmentPositionOffsetM: number;
}

export const MCS_020A_ENGLAND_WALES: Ruleset<NoiseRules> = {
  id: "mcs.noise-assessment",
  // 1.2.0 (2026-09-10): adds the Step 4 reflecting-surface distances, the Q
  // lookup, the Step 8 rounding and the assessment-position offset, each read
  // from the Issue 1.1 PDF (pages 7, 11-14). 1.1.0's figures are unchanged.
  version: "1.2.0",
  effectiveFrom: "2025-05-29", // England, SI 2025/560. Wales from 2026-06-01 (WSI 2026/79).
  jurisdictions: ["england", "wales"],
  provenance: "verified",
  source: {
    standard: "MCS 020 a)",
    issue: "1.1",
    clause: "1.2; Definitions (Assessment Position); Steps 3-8",
    issued: "2025-04-14",
    url: "https://mcscertified.com/wp-content/uploads/2025/05/MCS-020-a-Issue-1.1-Final.pdf",
  },
  data: {
    limitDb: 37.0,
    metric: "LAeq,5mins",
    barrierAttenuationDb: {
      "type-1": { "no-view": 10, "partial-view": 5, "full-view": 0 },
      "type-2": { "no-view": 5, "partial-view": 2.5, "full-view": 0 },
      none: { "no-view": 0, "partial-view": 0, "full-view": 0 },
    },
    // Q2 = one reflecting surface, Q4 = two, Q8 = three.
    // "A heat pump with more than three reflective surfaces ... will not meet MCS 020 a)."
    permittedDirectivityFactors: [2, 4, 8],
    distanceRoundingM: 0.1,
    reflectingSurfaceWithinM: 1,
    reflectingSurfaceMinExtentM: 1,
    directivityByReflectingSurfaces: { 1: 2, 2: 4, 3: 8 },
    resultRoundingDb: 0.1,
    assessmentPositionOffsetM: 1,
  },
};

/**
 * PROVISIONAL. Scotland and Northern Ireland still reference the legacy combined
 * MCS 020 (v1.3, 19/06/2019), whose 42 dB(A) limit is assessed against background
 * noise using a different procedure. The limit below is recorded for completeness
 * but the legacy METHOD is not implemented — the engine refuses compliance-mode
 * results from provisional rulesets, so a Scottish job will fail loudly rather
 * than silently produce an England-shaped answer.
 *
 * TODO: transcribe MCS 020 v1.3 and implement the background-noise method.
 */
export const MCS_020_LEGACY_SCOTLAND_NI: Ruleset<NoiseRules> = {
  id: "mcs.noise-assessment",
  version: "0.1.0-provisional",
  effectiveFrom: "2019-06-19",
  jurisdictions: ["scotland", "northern-ireland"],
  provenance: "provisional",
  source: {
    standard: "MCS 020",
    issue: "1.3",
    clause: "(not yet transcribed)",
    issued: "2019-06-19",
  },
  data: {
    limitDb: 42.0,
    metric: "dB(A) including background",
    barrierAttenuationDb: MCS_020A_ENGLAND_WALES.data.barrierAttenuationDb,
    permittedDirectivityFactors: [2, 4, 8],
    distanceRoundingM: 0.1,
    // Carried over from MCS 020 a) so the plan can describe the placement the
    // same way; the legacy method they belong to is not implemented (above).
    reflectingSurfaceWithinM: MCS_020A_ENGLAND_WALES.data.reflectingSurfaceWithinM,
    reflectingSurfaceMinExtentM: MCS_020A_ENGLAND_WALES.data.reflectingSurfaceMinExtentM,
    directivityByReflectingSurfaces: MCS_020A_ENGLAND_WALES.data.directivityByReflectingSurfaces,
    resultRoundingDb: MCS_020A_ENGLAND_WALES.data.resultRoundingDb,
    assessmentPositionOffsetM: MCS_020A_ENGLAND_WALES.data.assessmentPositionOffsetM,
  },
};

export const NOISE_RULESETS = [
  MCS_020A_ENGLAND_WALES,
  MCS_020_LEGACY_SCOTLAND_NI,
] as const;
